import {
  GetSignalingChannelEndpointCommand,
  KinesisVideoClient,
} from '@aws-sdk/client-kinesis-video';
import {
  GetIceServerConfigCommand,
  KinesisVideoSignalingClient,
  type IceServer,
} from '@aws-sdk/client-kinesis-video-signaling';
import type { FiloApiClient } from '@/api/FiloApiClient';

const CACHE_DURATION_MS = 4 * 60_000;

export const toUserFlowIceServers = (
  items: IceServer[] | undefined,
  region: string,
): RTCIceServer[] => [
  { urls: `stun:stun.kinesisvideo.${region}.amazonaws.com:443` },
  ...(items ?? []).filter((item) => item.Uris?.length).map((item) => ({
    urls: item.Uris!,
    username: item.Username,
    credential: item.Password,
  })),
];

export class UserFlowIceServerProvider {
  private cached: { expiresAt: number; servers: RTCIceServer[] } | null = null;
  private pending: Promise<RTCIceServer[]> | null = null;

  constructor(private readonly api: Pick<FiloApiClient, 'createUserFlowIce'>) {}

  get(): Promise<RTCIceServer[]> {
    if (this.cached && this.cached.expiresAt > Date.now()) return Promise.resolve(this.cached.servers);
    if (this.pending) return this.pending;
    this.pending = this.load().finally(() => { this.pending = null; });
    return this.pending;
  }

  private async load(): Promise<RTCIceServer[]> {
    const config = await this.api.createUserFlowIce();
    const credentials = {
      accessKeyId: config.credentials.accessKeyId,
      secretAccessKey: config.credentials.secretAccessKey,
      sessionToken: config.credentials.sessionToken,
    };
    const videoClient = new KinesisVideoClient({ region: config.region, credentials });
    let signalingClient: KinesisVideoSignalingClient | null = null;
    try {
      const endpoints = await videoClient.send(new GetSignalingChannelEndpointCommand({
        ChannelARN: config.channelArn,
        SingleMasterChannelEndpointConfiguration: { Protocols: ['HTTPS'], Role: 'VIEWER' },
      }));
      const httpsEndpoint = endpoints.ResourceEndpointList?.find(
        (endpoint) => endpoint.Protocol === 'HTTPS',
      )?.ResourceEndpoint;
      if (!httpsEndpoint) throw new Error('KVS ICE endpointを取得できませんでした');
      signalingClient = new KinesisVideoSignalingClient({
        region: config.region,
        endpoint: httpsEndpoint,
        credentials,
      });
      const response = await signalingClient.send(new GetIceServerConfigCommand({
        ChannelARN: config.channelArn,
      }));
      const servers = toUserFlowIceServers(response.IceServerList, config.region);
      if (!servers.some((server) => (
        Array.isArray(server.urls)
          ? server.urls.some((url) => url.startsWith('turn:') || url.startsWith('turns:'))
          : server.urls.startsWith('turn:') || server.urls.startsWith('turns:')
      ))) {
        throw new Error('TURNサーバーを取得できませんでした');
      }
      this.cached = { expiresAt: Date.now() + CACHE_DURATION_MS, servers };
      return servers;
    } finally {
      signalingClient?.destroy();
      videoClient.destroy();
    }
  }
}